import InfoIcon from "@/assets/icons/base64/InfoIcon"
import stakingHorizontalLine from "@/assets/images/staking-horizontal-line.png"
import stakingMascotImage from "@/assets/images/staking-mascot.png"
import stakingRectLineLg from "@/assets/images/staking-rect-line-lg.png"
import stakingRectLineLg2 from "@/assets/images/staking-rect-line-lg2.png"
import stakingRectLineMd from "@/assets/images/staking-rect-line-md.png"
import stakingVerticalLine from "@/assets/images/staking-vertical-line.png"
import { restakingVaultState } from "@/atoms/restakingState"
import { RelendTooltip } from "@/components/relendTooltip"
import breakpoints from "@/constants/breakpoints"
import useRestaking from "@/hooks/useRestaking"
import useTrans from "@/hooks/useTrans"
import useWindowSize from "@/hooks/useWindowSize"
import { LOCALE_EN, SKELETON_VALUE } from "@/utils/constants"
import { isValidNumber } from "@/utils/helpers"
import { formatNumberToDigits, formatPercent } from "@/utils/numberFormatter"
import { Box, Card, Container, Link, Typography } from "@mui/material"
import { useAtomValue } from "jotai"
import Image from "next/image"
import NextLink from "next/link"
import { useRouter } from "next/router"
import * as styles from "./styles"

const View = () => {
  const trans = useTrans()
  const { width } = useWindowSize()
  const { currentStakingAPY, currentTotalStakedAmount } = useRestaking()
  const restakingVault = useAtomValue(restakingVaultState)
  const { locale } = useRouter()
  const isLocaleEN = locale === LOCALE_EN

  const totalRELInSecureVault =
    currentTotalStakedAmount && isValidNumber(currentTotalStakedAmount)
      ? formatNumberToDigits(currentTotalStakedAmount)
      : SKELETON_VALUE
  const totalRewardPerDay =
    restakingVault && isValidNumber(restakingVault.vaultStat.emissionPerDay)
      ? formatNumberToDigits(restakingVault.vaultStat.emissionPerDay)
      : SKELETON_VALUE
  //  TODO: Will update this value when there is REL price
  // const totalRELInSecureVaultValue = restakingTotalStakedVault ? formatUsd(restakingTotalStakedVault) : SKELETON_VALUE
  // const totalRewardPerDayValue =
  //   restakingTotalStakedVault && restakingVault?.activeRewardApy
  //     ? formatNumberToDigits((restakingTotalStakedVault * restakingVault.activeRewardApy) / 365)
  //     : SKELETON_VALUE
  const stakingAPY =
    restakingVault && isValidNumber(currentStakingAPY) ? formatPercent(currentStakingAPY) : SKELETON_VALUE

  return (
    <Container maxWidth='xl'>
      <Box sx={styles.heroWrapper} data-testid='staking-overview'>
        <Box sx={styles.heroTitleImageWrapper}>
          <Box sx={styles.heroTitlePatternImage} className='hidden sm:block' />
          <Box sx={styles.heroTitleWrapper}>
            <Typography className='font-montserrat' sx={styles.heroTitle}>
              {trans.staking.hero.stake_earn_grow}
            </Typography>
            <Typography className='font-montserrat' sx={styles.heroTitle}>
              <Typography sx={styles.heroTitleRelend} component='span'>
                {trans.staking.hero.relend}
              </Typography>
              {trans.staking.hero.shape_future}
            </Typography>
          </Box>
          <Box sx={styles.heroDescriptionWrapper}>
            <Typography sx={styles.heroDescriptionBold}>
              {isLocaleEN ? trans.staking.hero.rel_holders : null}{" "}
              <Typography component='span' sx={styles.heroDescription}>
                {isLocaleEN ? trans.staking.hero.description : trans.staking.hero.description_p1_1}
              </Typography>{" "}
              {isLocaleEN ? null : trans.staking.hero.stake_rel}{" "}
              {isLocaleEN ? null : (
                <Typography component='span' sx={styles.heroDescription}>
                  {trans.staking.hero.description_p1_2}
                </Typography>
              )}
            </Typography>
            <Typography sx={styles.heroDescription2}>
              {isLocaleEN ? null : trans.staking.hero.description_p2}
            </Typography>
          </Box>
          <Link
            component={NextLink}
            href={trans.staking.hero.learn_more_link}
            target='_blank'
            sx={styles.heroLearnMore}
            data-testid='staking-overview-learn-more'
          >
            {trans.staking.hero.learn_more}
          </Link>
        </Box>
        <Box sx={styles.heroStatWrapper}>
          <Box sx={styles.heroStatCaro} />
          {width <= breakpoints.md ? (
            <Box sx={styles.heroMascotWrapper}>
              <Image src={stakingMascotImage} alt='staking-mascot-image' width={width <= breakpoints.sm ? 138 : 180} />
            </Box>
          ) : null}
          <Box sx={styles.heroMascotWrapper}>
            {width >= breakpoints.md ? (
              <Box position='relative'>
                <Box sx={styles.heroStatRectLineWrapper}>
                  <Image
                    src={
                      width >= breakpoints.lg2
                        ? stakingRectLineLg2
                        : width >= breakpoints.lg
                          ? stakingRectLineLg
                          : stakingRectLineMd
                    }
                    alt='staking-react-line'
                    width={224}
                  />
                </Box>
                <Image src={stakingMascotImage} alt='staking-mascot-image' width={224} style={styles.heroMascotImage} />
              </Box>
            ) : null}
          </Box>
          <Card sx={styles.heroStatCardBlack} data-testid='staking-overview-total-staked-rel-card'>
            <Box sx={styles.heroStatCardInside}>
              <Box sx={styles.heroStatCardBlackPattern} />
              <Typography sx={styles.heroStatCardBlackTitle}>{trans.staking.hero.total_staked_rel}</Typography>
              <Typography sx={styles.heroStatCardBlackStat}>{totalRELInSecureVault}</Typography>
              {/* <Typography sx={styles.heroStatCardBlackValue}>{totalRELInSecureVaultValue}</Typography> */}
              {width >= breakpoints.md ? (
                <Box sx={styles.heroStatVerticalLineWrapper}>
                  <Image src={stakingVerticalLine} alt='staking-vertical-line' height={77} />
                </Box>
              ) : null}
            </Box>
          </Card>
          <Card sx={styles.heroStatCardPink} data-testid='staking-overview-total-reward-per-day-card'>
            <Box sx={styles.heroStatCardInside}>
              <Box sx={styles.iconWrapper}>
                <Typography sx={styles.heroStatCardPinkTitle}>{trans.staking.hero.total_reward_per_day}</Typography>
                <RelendTooltip title={trans.staking.hero.total_reward_per_day_tooltip} arrow>
                  <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
                </RelendTooltip>
              </Box>
              <Typography sx={styles.heroStatCardPinkStat}>{totalRewardPerDay}</Typography>
              {/* <Typography sx={styles.heroStatCardPinkValue}>{formatUsd(totalRewardPerDayValue)}</Typography> */}
              {width >= breakpoints.md ? (
                <Box sx={styles.heroStatHorizontalLineWrapper}>
                  <Image src={stakingHorizontalLine} alt='staking-horizontal-line' width={100} />
                </Box>
              ) : null}
              <Box sx={styles.heroStatCardPinkPattern} />
            </Box>
          </Card>
          <Card sx={styles.heroStatCardGreen} data-testid='staking-overview-staking-apy-card'>
            <Box sx={styles.heroStatCardInside}>
              <Box sx={styles.heroStatCardGreenPattern} />
              <Typography sx={styles.heroStatCardBlackTitle}>{trans.staking.hero.staking_apy}</Typography>
              <Typography sx={styles.heroStatCardBlackStat}>{stakingAPY}</Typography>
            </Box>
          </Card>
        </Box>
      </Box>
    </Container>
  )
}

export default View
